import {
  Component, Input, Output,
  AfterViewInit, OnInit, OnDestroy,
  ViewChild, ViewChildren,
  EventEmitter, QueryList, ChangeDetectorRef
} from '@angular/core';
import { LMContextService } from '@services/lm-context.service';
import { LMContextUtilService } from '@utils/lm-context-util.service';
import { ExpandableAreaComponent, SohoDataGridComponent, SohoSearchFieldComponent } from 'ids-enterprise-ng';
import { animate, AUTO_STYLE, state, style, transition, trigger } from '@angular/animations';
import { LMReportsCatalogState } from '@states/models/lm-reports-catalog-state.model';
import {
  LMReport,
  LMReportCategoriesByModule,
  LMReportCategory,
  LMReportSearchModule,
  LMReportSearchValues
} from '../lm-reports';
import { LMReportsCatalogService } from '@report/catalog/lm-reports-catalog.service';

@Component({
  selector:    'lm-reports-search',
  templateUrl: 'lm-reports-search.component.html',
  styleUrls:   [ 'lm-reports-search.component.scss' ],
  animations:  [
  trigger('collapse', [
    state('false', style({ height: AUTO_STYLE, visibility: AUTO_STYLE })),
    state('true', style({ height: '0', visibility: 'hidden' })),
    transition('false => true', animate('100ms ease-in')),
    transition('true => false', animate('100ms ease-out'))
    ])]
  })

export class LMReportsSearchComponent implements AfterViewInit, OnInit, OnDestroy {
  public onlyMyReportsLabel = Soho.Locale.translate('ReportCatalogMyReports');
  public onlyMyReports = false;
  public onlyFavoritesLabel = Soho.Locale.translate('ReportCatalogFavoritesTab');
  public onlyFavorites = false;
  public hideDeliveredLabel = Soho.Locale.translate('ReportCatalogOrganizationReports');
  public hideDelivered = false;
  private ownerAdminId = this.lmContextService.getContext().actorId;
  public textSearchFields = {
    name: {
      id: 'name',
      name: 'title',
      placeholder: Soho.Locale.translate('Name'),
      value: '',
      index: 0 // Update for all search fields if they are re-arranged on screen
    },
    busClass: {
      id: 'busClass',
      name: 'busClass',
      placeholder: Soho.Locale.translate('CreateReportBusinessClassLabel'),
      value: '',
      index: 1
    },
    sharedWith: {
      id: 'sharedWith',
      name: 'sharedWith',
      placeholder: Soho.Locale.translate('Sharing'),
      value: '',
      index: 2
    },
    owner: {
      id: 'owner',
      name: 'owner',
      placeholder: Soho.Locale.translate('Owner'),
      value: '',
      index: 3
    }
  }
  public keywordFieldValue: string;
  public keywordPlaceholder: string = Soho.Locale.translate('Keyword');
  public reportsCatalogState: LMReportsCatalogState;
  public showSearchTags = false;

  // Advanced Options expandable section
  public advancedSearchTitle = Soho.Locale.translate('AdvancedSearchLabel');
  public advancedOptionsOpen = false;
  public buttonIcon = 'chevron-down';
  public chevronUp = $.createIcon({ icon: 'chevron-up', file: null });
  public chevronDown = $.createIcon({ icon: 'chevron-down', file: null });
  public moduleKeys: Array<string>;
  public searchTags: Array<LMListSearchTag> = [];
  public moduleObjectArray: Array<LMReportSearchModule> = [];
  private searchFormLoaded = false;
  public hideDeliveredReportsConfigFeature = false;

  private _categoriesByModule: LMReportCategoriesByModule;
  @Input() set categoriesByModule(categoriesByModule: LMReportCategoriesByModule) {
    this._categoriesByModule = categoriesByModule;
    if (this.searchFormLoaded) {
      this.buildModuleObjectArray();
    }
  }
  get categoriesByModule(): LMReportCategoriesByModule {
    return this._categoriesByModule;
  }
  @Input() categoryArray: Array<string>;
  @Input() reportListId: string;
  @Input() parentDataGrid: SohoDataGridComponent;

  @Output() displaySearchTags = new EventEmitter<any>();
  @Output() doKeywordSearch = new EventEmitter<any>();

  @ViewChild(ExpandableAreaComponent) expandableArea: ExpandableAreaComponent;
  @ViewChildren(SohoSearchFieldComponent) sohoSearchFields: QueryList<SohoSearchFieldComponent>;

  constructor(
    private lmCommonReportsService: LMReportsCatalogService,
    public  lmContextService: LMContextService,
    public  lmContextUtilService: LMContextUtilService,
    public  changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(){
    this.populateExistingSearchValues();
    if (this.moduleObjectArray?.length === 0) {
      this.buildModuleObjectArray();
    }
  }

  private populateExistingSearchValues() {
    const searchValues: LMReportSearchValues = this.lmCommonReportsService.getAllReportsSearchValues();
    if (searchValues) {
      this.textSearchFields = searchValues.textSearchFields;
      this.keywordFieldValue = searchValues.keywordFieldValue;
      this.onlyMyReports = searchValues.onlyMyReports;
      this.onlyFavorites = searchValues.onlyFavorites;
      this.hideDelivered = searchValues.hideDelivered;
      this.moduleObjectArray = searchValues.moduleObjectArray;
      setTimeout(() => { // Prevents ExpressionChangedAfterItHasBeenCheckedError
        this.buildAndApplyFilter();
      });
    }
  }

  private storeSearchValuesObject() {
    const values: LMReportSearchValues = {
      textSearchFields: this.textSearchFields,
      keywordFieldValue: this.keywordFieldValue,
      onlyMyReports: this.onlyMyReports,
      onlyFavorites: this.onlyFavorites,
      hideDelivered: this.hideDelivered,
      moduleObjectArray: this.moduleObjectArray
    };
    this.lmCommonReportsService.setAllReportsSearchValues(values);
  }

  public buildAndApplyFilter() {
    const filterCriteriaArray = [];
    this.searchTags = [];
    // Text Search Fields
    for (const key in this.textSearchFields) {
      if(this.textSearchFields[key]) {
        const field = this.textSearchFields[key];
        if(field.value !== '') {
          filterCriteriaArray.push({ columnId: field.name, operator: 'contains', value: field.value });
          this.addSearchTag('text-' + key, field.placeholder, field.value);
        }
      }
    }
    // Only My Reports switch
    if(this.onlyMyReports) {
      filterCriteriaArray.push({ columnId: 'owner', operator: 'equals', value: this.ownerAdminId });
      this.addSearchTag('switch-onlyMyReports', this.onlyMyReportsLabel, 'True');
    }
    // Only Favorites switch
    if(this.onlyFavorites) {
      filterCriteriaArray.push({ columnId: 'isFavorite', operator: 'selected', value: true });
      this.addSearchTag('switch-onlyFavorites', this.onlyFavoritesLabel, 'True');
    }
    if(this.hideDelivered) {
      filterCriteriaArray.push({ columnId: 'owner', operator: 'is-not-empty', value: '' });
      this.addSearchTag('switch-hideDelivered', this.hideDeliveredLabel, 'True');
    }
    // Category/Subcategory checkboxes
    const selectedCategories: Array<string> = [];
    this.moduleObjectArray.forEach(module => {
      if(module.hasSelectedSubcategories) {
        let allCategoriesChecked = false;
        if(module.numSelectedSubcategories === module.categoryArray.length) {
          allCategoriesChecked = true;
          const moduleTagId = 'category-' + (module.moduleId);
          this.addSearchTag(moduleTagId, module.moduleName, 'All');
        }
        module.categoryArray.forEach(category => {
          if(category.isChecked) {
            selectedCategories.push(module.moduleName + '_' + category.name.trim());
            if(!allCategoriesChecked) {
              const catTagId = 'category-' + module.moduleId + '-' + category.id;
              this.addSearchTag(catTagId, module.moduleName, category.name);
            }
          }
        });
      }
    });
    if(selectedCategories.length > 0) {
      filterCriteriaArray.push({ columnId: 'categoryUnique', operator: 'equals', filterType: 'multiselect', value: selectedCategories });
    }
    if(filterCriteriaArray.length > 0) {
      this.showSearchTags = true;
    } else {
      this.showSearchTags = false;
    }
    this.displaySearchTags.emit({ tags: this.searchTags });
    //console.log('Filer Criteria Array: ', filterCriteriaArray);
    this.parentDataGrid.applyFilter(filterCriteriaArray);
    // Commenting out Keyword search until it can work in tandem with the normal filter - see IDS issue 7713
    // if (this.keywordFieldValue?.length > 0) {
    //   this.doKeywordSearch.emit({ keyword: this.keywordFieldValue });
    //   this.addSearchTag('text_keyword', this.keywordPlaceholder, this.keywordFieldValue);
    // }
    this.storeSearchValuesObject();
  }

  buildModuleObjectArray() {
    if (this.categoriesByModule) {
      const newModuleObjectArray = [];
      this.moduleKeys = Object.keys(this.categoriesByModule).sort( (a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      let i = 0;
      for (const module of this.moduleKeys) {
        const existingModuleObj = this.moduleObjectArray.find((moduleObj) => moduleObj.moduleName === module);
        const catIdArray: Array<string> = []; // Used to prevent duplicate ids
        const categoryObjArray: Array<LMReportCategory> = [];
        this.categoriesByModule[module].forEach(category => {
          const catId = this.getUniqueCategoryId(catIdArray, category.toString());
          const existingCategoryObj = existingModuleObj ? existingModuleObj.categoryArray.find((cat) => cat.id === catId) : null;
          if (existingCategoryObj) { // If category already exists within existingModuleObj, add it as-is to retain selected filter
            categoryObjArray.push(existingCategoryObj);
          } else {
            categoryObjArray.push({ name: category.toString(), id: catId, isChecked: false });
          }
        });
        if (existingModuleObj) {
          existingModuleObj.categoryArray = categoryObjArray;
          existingModuleObj.collapsed = ((i++ > 2) && !(existingModuleObj.hasSelectedSubcategories));
          newModuleObjectArray.push(existingModuleObj);
        } else {
          newModuleObjectArray.push({
            moduleName: module,
            moduleId: module.replace(/[^A-Z0-9]/ig, ''),
            categoryArray: categoryObjArray,
            collapsed: (i++ > 2),
            hasSelectedSubcategories: false,
            numSelectedSubcategories: 0,
          });
        }
      }
      this.moduleObjectArray = newModuleObjectArray;
      setTimeout(() => { // Prevents ExpressionChangedAfterItHasBeenCheckedError
        this.searchTags = [];
        this.buildAndApplyFilter();
      });
    }
    this.storeSearchValuesObject();
    if (this.searchFormLoaded) {
      // Needed so that the Category/Subcategory checkboxes update when a report is added/removed
      this.changeDetector.detectChanges();
    }
  }

  private getUniqueCategoryId(catIdArray: Array<string>, category: string) {
    let catId = category.replace(/[^A-Z0-9]/ig, '');
    if (catIdArray.includes(catId)) {
      let inc = 1;
      let idWithInc = catId + '_' + inc;
      while (catIdArray.includes(idWithInc)) {
        inc += 1;
        idWithInc = catId + '_' + inc;
      }
      catId = idWithInc;
    }
    catIdArray.push(catId);
    return catId;
  }

  triggerKeywordSearch() {
    this.buildAndApplyFilter();
    // this.doKeywordSearch.emit({ keyword: this.keywordFieldValue });
    // if (this.keywordFieldValue.length > 0) {
    //   this.addSearchTag('keyword', this.keywordPlaceholder, this.keywordFieldValue);
    // }
    // this.displaySearchTags.emit({ tags: this.searchTags });
  }

  doTextSearch($event, field: any) {
    if($event.key === 'Backspace' || field.value?.length > 3) {
      this.buildAndApplyFilter();
    }
    if(field.value?.length === 0) {
      this.markSohoSearchFieldAsCleared(field.index);
    }
  }

  private markSohoSearchFieldAsCleared(index) {
    this.sohoSearchFields.get(index)?.clear(); // Removes X button from input element
  }

  clearTextSearch(field: any) {
    this.textSearchFields[field.id].value = '';
    this.buildAndApplyFilter();
  }

  categoryCheckboxOnChange($event: Event, moduleName: string, i: number) {
    const input: HTMLInputElement = <HTMLInputElement> $event.target;
    const module = this.moduleObjectArray[i];
    if(input.checked) {
      // input.classList.remove('partial');
      module.collapsed = false;
      module.numSelectedSubcategories = module.categoryArray.length;
      module.categoryArray.forEach(category => {
        category.isChecked = true;
      });
    } else {
      module.numSelectedSubcategories = 0;
      module.categoryArray.forEach(category => {
        category.isChecked = false;
      });
    }
    this.buildAndApplyFilter();
  }

  subcategoryCheckboxOnChange($event: Event, category: string, i: number, j: number) {
    // console.log('Checkbox clicked', $event, category);
    const input: HTMLInputElement = <HTMLInputElement> $event.target;
    const module = this.moduleObjectArray[i];
    if(input.checked) {
      module.numSelectedSubcategories++;
    } else {
      module.numSelectedSubcategories--;
    }
    module.hasSelectedSubcategories = module.numSelectedSubcategories > 0;
    this.buildAndApplyFilter();
  }

  addSearchTag(id: string, label: string, tag: string) {
    // NOTE: id should start with category_ | switch_ | text_ | keyword
    const i = this.searchTags.findIndex((t => t.id === id));
    const newTag = { label, id, tag, isRequired: false, isDisplayOnly: false, useFilterTypeNoValue: false };
    if (i === -1) {
      this.searchTags.push(newTag);
    } else {
      this.searchTags[i] = newTag;
    }
  }

  onSearchTagRemoved($event) {
    const indexArray = $event.id.split('-'); // Splits into [input type, input name | category, subcategory]
    if (indexArray[0] === 'text') {
      this.textSearchFields[indexArray[1]].value = '';
      this.markSohoSearchFieldAsCleared(this.textSearchFields[indexArray[1]].index);
    } else if (indexArray[0] === 'switch') {
      if (indexArray[1] === 'onlyMyReports') {
        this.onlyMyReports = false;
      }
      if (indexArray[1] === 'onlyFavorites') {
        this.onlyFavorites = false;
      }
      if (indexArray[1] === 'hideDelivered') {
        this.hideDelivered = false;
      }
    } else if (indexArray[0] === 'category') {
      const moduleIndex = this.moduleObjectArray.findIndex(r => r.moduleId === indexArray[1]);
      const module = this.moduleObjectArray[moduleIndex];
      if(indexArray.length === 2) {
        module.categoryArray.forEach(category => {
          category.isChecked = false;
        });
        module.numSelectedSubcategories = 0;
        module.hasSelectedSubcategories = false;
      } else if(indexArray.length === 3) {
        if (indexArray[3] === 'all') { // De-select all categories in the module
          module.hasSelectedSubcategories = false;
          module.numSelectedSubcategories = 0;
          module.categoryArray.forEach(category => {
            category.isChecked = false;
          });
        } else {
          const categoryIndex = module.categoryArray.findIndex(r => r.id === indexArray[2]);
          module.categoryArray[categoryIndex].isChecked = false;
          module.numSelectedSubcategories--;
          module.hasSelectedSubcategories = module.numSelectedSubcategories > 0;
        }
      } else {
        console.log('Invalid id in onSearchTagRemoved: ' + $event.id);
      }
    // } else if (indexArray[0] === 'keyword') { // Keyword currently not in use
    //   this.keywordFieldValue = '';
    } else {
      console.log('Unknown field type in onSearchTagRemoved: ' + $event.id);
    }
    this.buildAndApplyFilter();
  }

  onClearSearchTags() {
    this.searchTags = [];
    this.moduleObjectArray.forEach(module => {
      module.categoryArray.forEach(category => {
        category.isChecked = false;
      });
      module.hasSelectedSubcategories = false;
      module.numSelectedSubcategories = 0;
    });
    for (const key in this.textSearchFields) {
      if(this.textSearchFields[key] && this.textSearchFields[key].value) {
        this.textSearchFields[key].value = '';
        this.markSohoSearchFieldAsCleared(this.textSearchFields[key].index);
      }
    }
    this.onlyMyReports = false;
    this.onlyFavorites = false;
    this.hideDelivered = false;
    this.buildAndApplyFilter();
  }

  // For repeated module collapsible sections
  toggleCollapsible($event: Event, i: any) {
    if(!($event.target instanceof HTMLLabelElement) && !($event.target instanceof HTMLInputElement)) {
      // console.log('Collapsible clicked', $event, i);
      const header = <HTMLElement> $event.currentTarget;
      header.classList.toggle('active');
      this.moduleObjectArray[i].collapsed = !this.moduleObjectArray[i].collapsed;
    }
  }
  collapseAll(collapsed: boolean) {
    this.moduleObjectArray.forEach(module => {
      module.collapsed = collapsed;
    });
  }

  // For "Advanced Settings" expandable area
  onExpandableAreaClicked($event: MouseEvent) {
    $event.stopPropagation();
    $event.stopImmediatePropagation();
    if (this.expandableArea) {
      if (this.advancedOptionsOpen) {
        this.expandableArea.close();
        this.advancedOptionsOpen = false;
        this.buttonIcon = 'chevron-down';
      } else {
        this.expandableArea.open();
        this.advancedOptionsOpen = true;
        this.buttonIcon = 'chevron-up';
      }
    }
  }

  ngOnDestroy(): void {
  }

  ngAfterViewInit(): void {
    this.searchFormLoaded = true;
    setTimeout(() =>{
      this.hideDeliveredReportsConfigFeature = this.lmContextService.hideDeliveredReports();
    });
  }
}
